/**
 * Canonical US state / province names for storage, grouping,
 * filtering, and display. International values (Ontario, British
 * Columbia, …) pass through unchanged.
 *
 * Storage rule: `locations.state` and equivalent columns should
 * always hold the full name ("California", not "CA").
 */

export const UNKNOWN_STATE_LABEL = "Other / no state";

/** USPS abbreviations → canonical full display names. */
export const US_STATE_NAMES: Record<string, string> = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "Washington, D.C.",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  PR: "Puerto Rico",
  VI: "U.S. Virgin Islands",
  GU: "Guam",
};

/** Full state name (uppercased) → USPS code. */
const US_STATE_CODES: Record<string, string> = Object.fromEntries(
  Object.entries(US_STATE_NAMES).map(([code, name]) => [
    name.toUpperCase(),
    code,
  ]),
);

/** Legacy / import variants → canonical full name. */
const STATE_ALIASES: Record<string, string> = {
  "DISTRICT OF COLUMBIA": US_STATE_NAMES.DC,
  "D.C.": US_STATE_NAMES.DC,
  "WASHINGTON DC": US_STATE_NAMES.DC,
  "WASHINGTON, DC": US_STATE_NAMES.DC,
};

/**
 * Canonical full state name for persistence and grouping keys.
 * Empty in → empty out. Abbreviations and legacy spellings
 * normalize to the same full name ("CA" / "california" → "California").
 */
export function canonicalStateName(
  raw: string | null | undefined,
): string {
  const s = (raw ?? "").trim();
  if (!s) return "";
  const upper = s.toUpperCase();
  if (US_STATE_NAMES[upper]) return US_STATE_NAMES[upper];
  const alias = STATE_ALIASES[upper];
  if (alias) return alias;
  const code = US_STATE_CODES[upper];
  if (code) return US_STATE_NAMES[code];
  return s;
}

/**
 * Stable grouping / filter key — same as {@link canonicalStateName}
 * for every non-empty value so "CA" and "California" bucket together.
 */
export function normalizeStateKey(raw: string | null | undefined): string {
  return canonicalStateName(raw);
}

/** Display label — identical to canonical name; unknown values pass through. */
export function fullStateName(state: string | null | undefined): string {
  const canonical = canonicalStateName(state);
  if (!canonical) return UNKNOWN_STATE_LABEL;
  return canonical;
}

/**
 * Reverse of {@link canonicalStateName} — canonical full name (or any
 * recognized abbreviation/alias) → USPS 2-letter code. International /
 * unrecognized values pass through as their canonicalized form unchanged
 * (e.g. "Ontario" stays "Ontario" — there's no US code for it).
 *
 * Used where a display string wants the SHORT form (e.g. "Atlanta, GA
 * 30344") even though `locations.state` is stored as the full canonical
 * name per the storage rule above.
 */
export function stateAbbreviationOrOriginal(
  raw: string | null | undefined,
): string | null {
  const canonical = canonicalStateName(raw);
  if (!canonical) return null;
  return US_STATE_CODES[canonical.toUpperCase()] ?? canonical;
}

/** Case-insensitive equality after canonicalization. */
export function statesMatch(
  a: string | null | undefined,
  b: string | null | undefined,
): boolean {
  const left = canonicalStateName(a);
  const right = canonicalStateName(b);
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.localeCompare(right, undefined, { sensitivity: "base" }) === 0;
}

/** Normalize before writing to `locations.state` (null when blank). */
export function canonicalStateNameOrNull(
  raw: string | null | undefined,
): string | null {
  const canonical = canonicalStateName(raw);
  return canonical || null;
}

/** Canada Post abbreviations → province / territory names. */
export const CANADA_PROVINCE_NAMES: Record<string, string> = {
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

const CANADA_PROVINCE_CODES: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(CANADA_PROVINCE_NAMES).map(([code, name]) => [name.toUpperCase(), code]),
  ),
  QUÉBEC: "QC",
  NEWFOUNDLAND: "NL",
};

export type StateOrProvince = { label: string; country: "US" | "CA" | null };

/**
 * A stored state as a US state or a Canadian province, by name. Codes and
 * full names both resolve; anything else keeps its own spelling with no
 * country. Null when blank.
 */
export function stateOrProvince(raw: string | null | undefined): StateOrProvince | null {
  const s = (raw ?? "").trim();
  if (!s) return null;
  const upper = s.toUpperCase();
  const province = CANADA_PROVINCE_NAMES[upper] ?? CANADA_PROVINCE_NAMES[CANADA_PROVINCE_CODES[upper] ?? ""];
  if (province) return { label: province, country: "CA" };
  const us = canonicalStateName(s);
  if (US_STATE_CODES[us.toUpperCase()]) return { label: us, country: "US" };
  return { label: s, country: null };
}
